import "./CustomCursor.css";
import { useEffect, useRef } from "react";

const TRAIL = 0.45; // how quickly the circle catches up with the mouse (0–1 per frame; 1 = no trail)

/*
 * Custom cursor (mouse/trackpad only): a small white dot that trails the mouse.
 * - Over links and buttons it grows into a ring.
 * - Over elements with a data-cursor="Label" attribute it becomes a
 *   see-through, blurred bubble with that label (e.g. "Open").
 * - Over elements with a data-cursor-hide attribute it disappears, so the
 *   element's own hover effect does the talking (e.g. the play buttons).
 * - It shrinks a little while the mouse button is pressed.
 * Touch screens keep their normal behavior. With "reduce motion" turned on,
 * it follows the mouse exactly instead of trailing.
 */
function CustomCursor() {
  const outerRef = useRef(null);
  const labelRef = useRef(null);

  useEffect(() => {
    const finePointer = window.matchMedia?.("(hover: hover) and (pointer: fine)").matches;
    if (!finePointer) return undefined;
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    const outer = outerRef.current;
    const label = labelRef.current;
    document.documentElement.classList.add("has-custom-cursor");

    const target = { x: -100, y: -100 };
    const position = { x: -100, y: -100 };
    let frame = null;

    const render = () => {
      frame = null;
      const ease = reduceMotion ? 1 : TRAIL;
      position.x += (target.x - position.x) * ease;
      position.y += (target.y - position.y) * ease;
      outer.style.transform = `translate3d(${position.x}px, ${position.y}px, 0)`;
      // Keep moving until it has caught up
      if (Math.abs(target.x - position.x) > 0.1 || Math.abs(target.y - position.y) > 0.1) {
        frame = requestAnimationFrame(render);
      }
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(render);
    };

    const onMove = (event) => {
      target.x = event.clientX;
      target.y = event.clientY;
      outer.classList.add("is-visible");
      updateLook(event.target);
      schedule();
    };

    // What is under the mouse decides the cursor's look
    const updateLook = (node) => {
      const element = node instanceof Element ? node : null;
      outer.classList.toggle("is-hidden", Boolean(element?.closest("[data-cursor-hide]")));
      const interactive = element?.closest("a, button, [role='button'], [role='tab'], label");
      let labelled = element?.closest("[data-cursor]");
      // A button inside a labelled area gets the normal ring, not the area's label
      if (labelled && interactive && interactive !== labelled && labelled.contains(interactive)) {
        labelled = null;
      }
      outer.classList.toggle("is-label", Boolean(labelled));
      outer.classList.toggle("is-hover", !labelled && Boolean(interactive));
      label.textContent = labelled ? labelled.getAttribute("data-cursor") : "";
    };
    const onOver = (event) => updateLook(event.target);

    const onDown = () => outer.classList.add("is-pressed");
    const onUp = () => outer.classList.remove("is-pressed");
    const onLeave = () => outer.classList.remove("is-visible");

    window.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mouseover", onOver);
    window.addEventListener("mousedown", onDown);
    window.addEventListener("mouseup", onUp);
    document.documentElement.addEventListener("mouseleave", onLeave);

    return () => {
      cancelAnimationFrame(frame);
      document.documentElement.classList.remove("has-custom-cursor");
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseover", onOver);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("mouseup", onUp);
      document.documentElement.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return (
    <div className="cursor" ref={outerRef} aria-hidden="true">
      <div className="cursor-ball">
        <span className="cursor-label" ref={labelRef} />
      </div>
    </div>
  );
}

export default CustomCursor;
